import { PMScores, PMSubjectReport, PMDetailReport, PMBandDef } from './types';

export type CellKind = 'pct' | 'int' | 'growth';

export interface CellValue {
  value: number | null;
  kind: CellKind;
  tooltip?: string;
  /** Inline "correct/asked" next to a percentage, e.g. "7/10". */
  counts?: string;
}

export interface LeafColumn {
  id: string;
  group: string;
  column: string;
  columnTooltip?: string;
  stat: string;
  render: (scores: PMScores) => CellValue;
}

export type ScoreMode = 'weighted' | 'raw';

const countsLabel = (cell?: { correct?: number; asked?: number }) =>
  cell && cell.asked !== undefined ? `${cell.correct}/${cell.asked}` : undefined;

const countsTooltip = (cell?: { correct?: number; asked?: number }) => {
  const label = countsLabel(cell);
  return label ? `${label} correct` : undefined;
};

export const bandLabel = (band: PMBandDef) => `${band.label}`;

export const buildSummaryColumns = (
  subject: PMSubjectReport,
  periodLabel: string,
  compareLabel: string | null,
  bands: PMBandDef[],
  mode: ScoreMode,
): LeafColumn[] => {
  const overallField = mode === 'raw' ? 'raw_pct' : 'overall_pct';
  const growthField = mode === 'raw' ? 'raw_growth' : 'growth';
  const bandsField = mode === 'raw' ? 'raw_band_distribution' : 'band_distribution';

  const leaves: LeafColumn[] = [];

  if (compareLabel) {
    leaves.push(
      {
        id: 'cmp-students', group: compareLabel, column: 'Total Students Tested', stat: '',
        render: (s) => ({ value: s.compare?.student_count ?? null, kind: 'int' }),
      },
      {
        id: 'cmp-overall', group: compareLabel, column: 'Overall Score Average', stat: '',
        render: (s) => ({
          value: (mode === 'raw' ? s.compare?.raw_pct : s.compare?.overall_pct) ?? null,
          kind: 'pct',
        }),
      },
    );
  }

  leaves.push(
    {
      id: 'cur-students', group: periodLabel, column: 'Total Students Tested', stat: '',
      render: (s) => ({ value: s.student_count, kind: 'int' }),
    },
    {
      id: 'cur-overall', group: periodLabel, column: 'Overall Score Average', stat: '',
      render: (s) => ({ value: s[overallField], kind: 'pct' }),
    },
  );

  if (compareLabel) {
    leaves.push({
      id: 'growth', group: `${periodLabel}-${compareLabel}`, column: 'Growth', stat: '',
      render: (s) => ({ value: s[growthField], kind: 'growth' }),
    });
  }

  bands.forEach((band, index) => {
    leaves.push({
      id: `band-${index}`, group: `${periodLabel} Score Distribution`,
      column: bandLabel(band), stat: '',
      render: (s) => ({ value: s[bandsField]?.[index] ?? null, kind: 'pct' }),
    });
  });

  subject.category_order.forEach((category) => {
    leaves.push({
      id: `cat-${category}`, group: 'Reporting Category Scores', column: category, stat: '',
      render: (s) => {
        const cell = s.categories[category];
        return {
          value: (mode === 'raw' ? cell?.raw_pct : cell?.pct) ?? null,
          kind: 'pct',
          tooltip: countsTooltip(cell),
        };
      },
    });
  });

  return leaves;
};

export const buildBloomsColumns = (detail: PMDetailReport): LeafColumn[] => {
  const leaves: LeafColumn[] = [];
  detail.category_order.forEach((level) => {
    leaves.push(
      {
        id: `${level}-pct`, group: 'Overall', column: level, stat: '% of Questions',
        render: (s) => {
          const cell = s.categories[level];
          return {
            value: cell?.pct ?? null,
            kind: 'pct',
            tooltip: countsTooltip(cell),
            counts: countsLabel(cell),
          };
        },
      },
      {
        id: `${level}-na`, group: 'Overall', column: level, stat: 'No Answer',
        render: (s) => ({ value: s.categories[level]?.no_answer ?? null, kind: 'int' }),
      },
    );
  });
  leaves.push(
    {
      id: 'not-asked-pct', group: 'Unanswered Questions', column: '', stat: '% Unanswered',
      render: (s) => ({ value: s.not_asked_pct ?? null, kind: 'pct' }),
    },
    {
      id: 'not-asked-count', group: 'Unanswered Questions', column: '', stat: '# Unanswered',
      render: (s) => ({ value: s.not_asked_count ?? null, kind: 'int' }),
    },
  );
  return leaves;
};

export const buildStandardsColumns = (detail: PMDetailReport): LeafColumn[] => {
  const leaves: LeafColumn[] = [];
  detail.category_order.forEach((code) => {
    const group = detail.column_groups?.[code] || '';
    const tooltip = detail.column_labels?.[code];
    leaves.push(
      {
        id: `${code}-pct`, group, column: code, columnTooltip: tooltip, stat: 'Performance %',
        render: (s) => {
          const cell = s.categories[code];
          return {
            value: cell?.pct ?? null,
            kind: 'pct',
            tooltip: countsTooltip(cell),
            counts: countsLabel(cell),
          };
        },
      },
      {
        id: `${code}-q`, group, column: code, columnTooltip: tooltip, stat: 'Questions',
        render: (s) => ({ value: s.categories[code]?.asked ?? null, kind: 'int' }),
      },
    );
  });
  return leaves;
};
